import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Home, Search, SquarePlus, MessageCircle, User } from 'lucide-react';
import { formatBadge, useUnreadCount } from '../../chat/hooks/useUnreadCount';
import { useAuthStore } from '../../store/useAuthStore';

interface BottomNavProps {
  mobileMenuOpen: boolean;
}

// Brand blue used everywhere else in the app (logo text, CTAs, active states) —
// see Header.tsx's "text-blue-600" logo and hover states.
const BRAND_BLUE = '#2563eb';

const BottomNav: React.FC<BottomNavProps> = ({ mobileMenuOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Two separate primitive selectors, not one selector returning a new
  // object literal — zustand's useSyncExternalStore compares by reference,
  // so a fresh object every render trips "getSnapshot should be cached" and
  // spirals into a "Maximum update depth exceeded" render loop.
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const avatar = useAuthStore((state) => state.avatar);
  const unreadBadge = formatBadge(useUnreadCount());

  const [active, setActive] = useState(location.pathname);
  const [pressed, setPressed] = useState<string | null>(null);
  const [compact, setCompact] = useState(false);
  // While a text field is focused the keyboard is up: on Android the fixed nav
  // rides up with it and covers the field, so it steps aside until blur.
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isField = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
      if (el.tagName !== 'INPUT') return false;
      return !['checkbox', 'radio', 'range', 'button', 'submit', 'file', 'color'].includes((el as HTMLInputElement).type);
    };
    const onIn = (e: FocusEvent) => isField(e.target) && setTyping(true);
    const onOut = () => setTimeout(() => setTyping(isField(document.activeElement)), 0);
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);

  useEffect(() => {
    setActive(location.pathname);
  }, [location.pathname]);

  // Instagram-style behavior: the whole bar shrinks (as one piece, via a
  // uniform scale — not just its height/padding) while the user is
  // scrolling down through content, and relaxes back to full size on
  // scroll up or near the top.
  const lastY = useRef(0);
  const ticking = useRef(false);
  useEffect(() => {
    lastY.current = window.scrollY;

    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY.current;

        if (y < 24) {
          setCompact(false);
        } else if (delta > 4) {
          setCompact(true);
        } else if (delta < -4) {
          setCompact(false);
        }

        lastY.current = y;
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const tabs = [
    { path: '/', icon: Home, label: t('nav.home', 'Главная') },
    { path: '/listings', icon: Search, label: t('nav.browse', 'Объявления') },
    { path: '/create-listing', icon: SquarePlus, label: t('nav.post', 'Разместить') },
    // Favorites moved to the mobile header (Header.tsx) to make room for Messages.
    {
      path: '/messages',
      icon: MessageCircle,
      label: t('nav.messages', 'Сообщения'),
      badge: unreadBadge,
    },
    {
      path: '/profile',
      icon: User,
      label: t('nav.profile', 'Профиль'),
      isProfile: true,
    },
  ];

  return (
    <div
      data-chrome-wrap="bottom-nav"
      className={`fixed inset-x-0 bottom-0 z-[60] md:hidden transition-transform duration-300 ease-out ${
        mobileMenuOpen || typing
          ? 'translate-y-full opacity-0 pointer-events-none'
          : 'translate-y-0 opacity-100 pointer-events-auto'
      }`}
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px) + 12px, 22px)',
        paddingLeft: 'max(env(safe-area-inset-left, 0px), 12px)',
        paddingRight: 'max(env(safe-area-inset-right, 0px), 12px)',
      }}
    >
      <nav
        data-chrome="bottom-nav"
        aria-label={t('nav.mobileNavigation', 'Мобильная навигация')}
        className="
          mx-auto flex w-full max-w-md items-center justify-between
          rounded-full bg-white
          px-4 py-2.5
          shadow-[0_8px_30px_rgba(15,23,42,0.12)]
          ring-1 ring-gray-100
          transition-transform duration-300 ease-out
        "
        style={{
          transform: compact ? 'scale(0.86)' : 'scale(1)',
          transformOrigin: 'bottom center',
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          // /messages/:id keeps the Messages tab lit.
          const isActive =
            tab.path === '/messages' ? active.startsWith('/messages') : active === tab.path;
          const isPressed = pressed === tab.path;
          const showAvatar =
            tab.isProfile && isAuthenticated && avatar && avatar.includes('http');

          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              onPointerDown={() => setPressed(tab.path)}
              onPointerUp={() => setPressed(null)}
              onPointerLeave={() => setPressed(null)}
              aria-label={
                tab.badge
                  ? `${tab.label}, ${t('chat.aria.unreadBadge', { count: tab.badge })}`
                  : tab.label
              }
              aria-current={isActive ? 'page' : undefined}
              className="relative flex flex-1 select-none items-center justify-center"
              style={{
                WebkitTapHighlightColor: 'transparent',
                transform: isPressed ? 'scale(0.88)' : 'scale(1)',
                transition: 'transform 0.15s ease-out',
              }}
            >
              <span
                className={`
                  relative flex h-10 w-10 items-center justify-center rounded-full
                  transition-colors duration-300 ease-out
                  ${isActive ? 'bg-blue-50' : 'bg-transparent'}
                `}
              >
                {tab.badge && (
                  <span className="absolute -right-0.5 top-0 min-w-[18px] rounded-full bg-blue-600 px-1 py-px text-center text-[10px] font-bold leading-[14px] text-white ring-2 ring-white">
                    {tab.badge}
                  </span>
                )}
                {showAvatar ? (
                  <img
                    src={avatar as string}
                    alt=""
                    className={`
                      h-7 w-7 rounded-full object-cover
                      transition-all duration-300 ease-out
                      ${isActive ? 'ring-2 ring-blue-600' : 'ring-1 ring-gray-200'}
                    `}
                  />
                ) : (
                  <Icon
                    className="h-[22px] w-[22px] transition-colors duration-300 ease-out"
                    strokeWidth={isActive ? 2.4 : 1.9}
                    style={{ color: isActive ? BRAND_BLUE : '#9ca3af' }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default BottomNav;
